import { ErrorBoundary } from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { capitalize } from 'es-toolkit';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { cancelSubscription, fetchSubscription } from '@/entities/payment/api';
import { SettingPageHeader } from '../ui/setting.header';
import { SettingSectionLayout } from '../ui/setting.section';

import type { Subscription } from '@/entities/payment';
import { SUBSCRIPTION_STATUS } from '@/entities/payment/constants';
import type { Workspace } from '@/entities/workspace/types';
import { RingSpinner } from '@/shared/components/loader/ring-spinner';
import { Button } from '@/shared/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import { Stage } from '@/shared/components/stage/stage';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { formatUSDCurrency } from '@/shared/utils/fotmat-currency';

export function BillingPage() {
  const { workspace } = useWorkspace();
  const workspaceId = workspace.id;
  const { data: subscription, isLoading: isSubscriptionLoading } = useQuery({
    queryKey: ['subscription', workspaceId],
    queryFn: () => fetchSubscription(workspaceId),
  });

  return (
    <SettingSectionLayout>
      <SettingPageHeader title="Billing" />
      <Stage>
        <Stage.Scene status={workspace}>
          {(workspace) => (
            <Stage>
              <Stage.Scene status={subscription}>
                {(subscription) => (
                  <>
                    <BillingSection workspace={workspace} subscription={subscription} />
                    <div className="h-20" />
                    <SettingPageHeader title="Payment details" />
                    <PaymentDetailsSection subscription={subscription} />
                    <div className="h-20" />
                    <SettingPageHeader title="Invoices" />
                    <ErrorBoundary
                      fallback={({ error, resetError }) => {
                        const errorMessage =
                          error instanceof Error ? error.message : 'Unknown error';
                        return (
                          <div>
                            Error: {errorMessage}
                            <Button onClick={resetError}>Retry</Button>
                          </div>
                        );
                      }}
                    >
                      <InvoicesSection subscription={subscription} />
                    </ErrorBoundary>
                  </>
                )}
              </Stage.Scene>
              <Stage.Scene status={isSubscriptionLoading}>
                <RingSpinner />
              </Stage.Scene>
              <Stage.Scene default>
                <div className="flex text-zinc-500">No billing info</div>
              </Stage.Scene>
            </Stage>
          )}
        </Stage.Scene>
        <Stage.Scene default>
          <div className="flex text-zinc-500">No workspace</div>
        </Stage.Scene>
      </Stage>
    </SettingSectionLayout>
  );
}

function BillingSection({
  workspace,
  subscription,
}: {
  workspace: Workspace;
  subscription: Subscription;
}) {
  const [isCancelSubscriptionDialogOpen, setIsCancelSubscriptionDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { plan } = workspace;
  const { amount, interval, period_end } = subscription;
  const { workspaceId } = useWorkspaceId();
  const { month, day, year } = period_end;

  const description = useMemo(() => {
    const expiredDate = `${month}/${day}/${year}`;
    if (subscription.status === SUBSCRIPTION_STATUS.CANCELED) {
      return `${formatUSDCurrency(amount)} per ${interval} · Available until ${expiredDate}`;
    }
    return `${formatUSDCurrency(amount)} per ${interval} · Renews ${expiredDate}`;
  }, [amount, interval, month, day, year, subscription.status]);

  return (
    <div className="flex flex-col">
      <Row
        label="Plan"
        value={`${capitalize(plan)} / ${capitalize(subscription.status)}`}
        description={description}
        buttonLabel={subscription.status === SUBSCRIPTION_STATUS.ACTIVE ? 'Cancel' : undefined}
        onClick={() => {
          if (subscription.status === SUBSCRIPTION_STATUS.ACTIVE) {
            setIsCancelSubscriptionDialogOpen(true);
          } else {
            navigate({
              to: '/workspace/$workspaceId/settings/plan',
              params: {
                workspaceId,
              },
            });
          }
        }}
      />
      <Dialog
        open={isCancelSubscriptionDialogOpen}
        onOpenChange={setIsCancelSubscriptionDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel subscription</DialogTitle>
          </DialogHeader>
          <DialogDescription>Are you sure you want to cancel your subscription?</DialogDescription>
          <DialogFooter>
            <Button
              variant="destructive"
              onClick={async () => {
                try {
                  await cancelSubscription(subscription.id);
                  setIsCancelSubscriptionDialogOpen(false);
                  toast.success('Subscription cancelled');
                  navigate({
                    to: '/workspace/$workspaceId',
                    params: {
                      workspaceId,
                    },
                  });
                } catch (error) {
                  console.error(error);
                  toast.error('Failed to cancel subscription');
                }
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PaymentDetailsSection({ subscription }: { subscription: Subscription }) {
  return (
    <div className="flex flex-col">
      <Row label="Payment method" value="Credit card" />
      <Row label="Billed to" value={subscription.user_name} />
      <Row label="Billing email" value={subscription.user_email} />
    </div>
  );
}

function InvoicesSection({ subscription }: { subscription: Subscription }) {
  return (
    <div className="flex flex-col">
      <Stage>
        <Stage.Scene status={!subscription.invoices || subscription.invoices?.length === 0}>
          <div className="flex text-zinc-500">No invoices</div>
        </Stage.Scene>
        <Stage.Scene status={subscription.invoices?.length > 0}>
          {subscription.invoices?.map((invoice) => (
            <Row
              key={invoice.created_at.day}
              label={`${invoice.created_at.month}/${invoice.created_at.day}/${invoice.created_at.year}`}
              value={`Paid, ${formatUSDCurrency(invoice.amount)}`}
              buttonLabel="View invoice"
              onClick={() => {
                window.open(invoice.url, '_blank');
              }}
            />
          ))}
        </Stage.Scene>
      </Stage>
    </div>
  );
}

function Row({
  label,
  value,
  description,
  buttonLabel,
  onClick,
}: {
  label: React.ReactNode;
  value?: React.ReactNode;
  description?: React.ReactNode;
  buttonLabel?: string;
  onClick?: () => void;
}) {
  return (
    <div className="flex items-center justify-between border-b py-3 last:border-b-0">
      <div className="flex min-w-0 flex-1 flex-col text-sm font-medium">
        <div className="text-foreground flex text-sm">{label}</div>
        <div className="text-muted-foreground flex font-normal">{value}</div>
      </div>
      <div className="text-foreground min-w-0 flex-1 truncate text-right text-sm">
        {description}
      </div>
      {buttonLabel && (
        <Button
          variant="outline"
          size="sm"
          className="ml-4 w-40 shrink-0"
          type="button"
          onClick={onClick}
        >
          {buttonLabel}
        </Button>
      )}
    </div>
  );
}
